import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Plus, Shield } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  createRole,
  deleteRole,
  fetchAssignablePermissions,
  fetchRoles,
  updateRole,
} from '@/lib/api/roles'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

const GROUP_LABELS = {
  menu: 'Menu items',
  customers: 'Customer actions',
  appointments: 'Appointments',
  pos: 'POS',
  reports: 'Sales',
  admin: 'Admin',
}

function emptyForm() {
  return {
    name: '',
    description: '',
    permission_ids: [],
  }
}

function roleToForm(role) {
  return {
    name: role.name ?? '',
    description: role.description ?? '',
    permission_ids: role.permission_ids ?? role.permissions?.map((item) => item.id) ?? [],
  }
}

export function RolesSetupPage() {
  const [roles, setRoles] = useState([])
  const [permissions, setPermissions] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [creating, setCreating] = useState(false)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState(emptyForm())

  const groupedPermissions = useMemo(() => {
    /** @type {Record<string, typeof permissions>} */
    const groups = {}
    permissions.forEach((permission) => {
      const group = permission.group ?? 'other'
      if (!groups[group]) groups[group] = []
      groups[group].push(permission)
    })
    return groups
  }, [permissions])

  const loadData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [roleItems, permissionItems] = await Promise.all([
        fetchRoles(),
        fetchAssignablePermissions(),
      ])
      setRoles(roleItems)
      setPermissions(permissionItems)
      return { roleItems, permissionItems }
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load roles.'))
      return { roleItems: [], permissionItems: [] }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let active = true

    async function init() {
      const { roleItems } = await loadData()
      if (!active) return

      const customRoles = roleItems.filter((role) => !role.is_system)
      if (customRoles.length > 0) {
        setSelectedId(customRoles[0].id)
        setForm(roleToForm(customRoles[0]))
        setCreating(false)
      } else {
        setCreating(true)
        setForm(emptyForm())
      }
    }

    init()
    return () => {
      active = false
    }
  }, [loadData])

  const selectedRole = roles.find((role) => role.id === selectedId) ?? null

  function startCreate() {
    setCreating(true)
    setSelectedId(null)
    setForm(emptyForm())
    setSuccess('')
    setError('')
  }

  function selectRole(role) {
    setCreating(false)
    setSelectedId(role.id)
    setForm(roleToForm(role))
    setSuccess('')
    setError('')
  }

  function togglePermission(permissionId) {
    setForm((current) => {
      const ids = new Set(current.permission_ids)
      if (ids.has(permissionId)) {
        ids.delete(permissionId)
      } else {
        ids.add(permissionId)
      }
      return { ...current, permission_ids: Array.from(ids) }
    })
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      if (creating) {
        const role = await createRole(form)
        await loadData()
        setCreating(false)
        setSelectedId(role.id)
        setForm(roleToForm(role))
        setSuccess('Role created.')
      } else if (selectedId) {
        const role = await updateRole(selectedId, form)
        setRoles((current) => current.map((item) => (item.id === role.id ? role : item)))
        setForm(roleToForm(role))
        setSuccess('Role saved.')
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to save role.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete() {
    if (!selectedRole || selectedRole.is_system) return
    if (!window.confirm(`Delete role "${selectedRole.name}"?`)) return

    setSubmitting(true)
    setError('')
    try {
      await deleteRole(selectedRole.id)
      const { roleItems } = await loadData()
      const next = roleItems.find((role) => !role.is_system)
      if (next) {
        selectRole(next)
      } else {
        startCreate()
      }
      setSuccess('Role deleted.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to delete role.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="salon-page py-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Shield className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Roles & access</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Create roles, choose menu items, and control what staff can do.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" className="rounded-xl" asChild>
            <Link to="/admin/users">Manage users</Link>
          </Button>
          <Button type="button" className="rounded-xl" onClick={startCreate}>
            <Plus className="h-4 w-4" />
            New role
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <aside className="salon-panel p-3">
            <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Roles
            </p>
            <div className="space-y-1">
              {roles.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => selectRole(role)}
                  className={cn(
                    'flex w-full flex-col rounded-xl px-3 py-2 text-left text-sm transition-colors',
                    selectedId === role.id && !creating
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-muted/60',
                  )}
                >
                  <span className="font-medium">{role.name}</span>
                  {role.is_system ? (
                    <span className="text-xs text-muted-foreground">Built-in</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Custom</span>
                  )}
                </button>
              ))}
            </div>
          </aside>

          <form onSubmit={handleSubmit} className="salon-panel space-y-6 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="mb-1.5 block font-medium">Role name</span>
                <input
                  value={form.name}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  className="h-11 w-full rounded-xl border border-border/80 bg-card px-4"
                  required
                  disabled={selectedRole?.is_system}
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="mb-1.5 block font-medium">Description (optional)</span>
                <input
                  value={form.description}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                  className="h-11 w-full rounded-xl border border-border/80 bg-card px-4"
                  disabled={selectedRole?.is_system}
                />
              </label>
            </div>

            {selectedRole?.is_system ? (
              <p className="rounded-xl bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                Built-in roles cannot be changed. Create a custom role to pick menu items and features.
              </p>
            ) : (
              <div className="space-y-5">
                {Object.entries(groupedPermissions).map(([group, items]) => (
                  <fieldset key={group} className="space-y-2">
                    <legend className="text-sm font-semibold">
                      {GROUP_LABELS[group] ?? group}
                    </legend>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {items.map((permission) => (
                        <label
                          key={permission.id}
                          className="flex items-start gap-2 rounded-xl border border-border/60 px-3 py-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={form.permission_ids.includes(permission.id)}
                            onChange={() => togglePermission(permission.id)}
                          />
                          <span>{permission.name}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>
            )}

            {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}
            {success ? <p className="text-sm text-primary" role="status">{success}</p> : null}

            <div className="flex flex-wrap gap-2">
              {!selectedRole?.is_system ? (
                <Button type="submit" disabled={submitting} className="rounded-xl">
                  {submitting ? 'Saving…' : creating ? 'Create role' : 'Save role'}
                </Button>
              ) : null}
              {!creating && selectedRole && !selectedRole.is_system ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={submitting}
                  onClick={handleDelete}
                  className="rounded-xl text-destructive"
                >
                  Delete role
                </Button>
              ) : null}
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
